import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Seamlessly render a Preact component in a React project",
  "description": "",
  "published": "2018-02-28T00:00:00.000Z",
  "redirect_from": ["/blog/seamlessly-render-preact-component-react-project/swizec/8224"],
  "categories": "Front End, preact, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "sdxz_1A8zFk",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Have you ever wanted to render a `}<a parentName="p" {...{
        "href": "https://preactjs.com/"
      }}>{`Preact`}</a>{` component inside a React project?`}</p>
    <p>{`It's hard, right? First of all, why? `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/building-interactive-dom-benchmark-preliminary-results/swizec/8219"
      }}>{`Because`}</a>{`. Second of all, how do you reconcile the two different implementations of JSX?`}</p>
    <p>{`Preact and React are `}<a parentName="p" {...{
        "href": "https://github.com/developit/preact/wiki/Differences-to-React"
      }}>{`pretty similar`}</a>{`, you see. Both are based on components, both leverage JSX to make your life easier, and for the most part, they look interchangeable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// React component
import React from 'react';
const Header => <h1>I am a header</h1>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Preact component
import Preact from 'preact';
const Header => <h1>I am a header</h1>
`}</code></pre>
    <p>{`Where they differ greatly is what that `}<inlineCode parentName="p">{`<h1>I am a header</h1>`}</inlineCode>{` compiles to. For React, it's a `}<inlineCode parentName="p">{`createElement()`}</inlineCode>{` call. For Preact ,it's a `}<inlineCode parentName="p">{`h()`}</inlineCode>{` call.`}</p>
    <p>{`And that's where the trouble begins.`}</p>
    <h2 {...{
      "id": "reconciling-createelement-and-h"
    }}>{`Reconciling createElement() and h()`}</h2>
    <p>{`I got this idea from Jason himself.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Set your pragma to \`h\` and conditionally assign h within the scope you want:`}<br parentName="p"></br><br parentName="p"></br>{`/** `}<a parentName="p" {...{
          "href": "https://twitter.com/jsx"
        }}>{`@jsx`}</a>{` h */`}<br parentName="p"></br>{`import React from 'react'`}<br parentName="p"></br>{`import { h } from 'preact'`}<br parentName="p"></br><br parentName="p"></br>{`const WithReact = () => {`}<br parentName="p"></br>{`  let h = React.createElement;`}<br parentName="p"></br>{`  return <div>react</div>`}<br parentName="p"></br>{`}`}<br parentName="p"></br><br parentName="p"></br>{`const WithPreact = () => (`}<br parentName="p"></br>{`  <div>preact</div>`}<br parentName="p"></br>{`)`}</p>{`— Jason Miller 🦊⚛ (@_developit) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/_developit/status/968143181694033921"
      }}>{`February 26, 2018`}</a></blockquote>
    <p>{`Here's how it looks in code 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/react-wrap-preact.png",
        "alt": null
      }}></img></p>
    <p>{`There are 4 parts to this:`}</p>
    <ol>
      <li parentName="ol">{`Tell Babel to use `}<inlineCode parentName="li">{`h`}</inlineCode>{` for the JSX function. That's the `}<inlineCode parentName="li">{`/** @jsx h */`}</inlineCode>{` magic comment at the top.`}</li>
      <li parentName="ol">{`Import both React and Preact. You'll need both.`}</li>
      <li parentName="ol">{`Make a Preact component. Or take one from somewhere else. It doesn't matter. Whatever you want :)`}</li>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`<Wrapper />`}</inlineCode>{` React component`}</li>
    </ol>
    <p>{`That `}<inlineCode parentName="p">{`<Wrapper />`}</inlineCode>{` component is where the magic happens.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`render()`}</inlineCode>{` method sets `}<inlineCode parentName="p">{`h`}</inlineCode>{` to `}<inlineCode parentName="p">{`React.createElement`}</inlineCode>{` and outputs an anchor div. Setting `}<inlineCode parentName="p">{`h`}</inlineCode>{` ensures that this part of JSX compiles into React's `}<inlineCode parentName="p">{`createElement`}</inlineCode>{` calls. This makes the component integrate seamlessly with the rest of your project.`}</p>
    <p>{`Then we hook into the component lifecycle with `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`. We call `}<inlineCode parentName="p">{`renderPreact`}</inlineCode>{` in both of them to ensure our wrapper component always ends up rendering Preact.`}</p>
    <p>{`Same as my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/translate-random-d3-example-react/swizec/7765"
      }}>{`React D3 blackbox`}</a>{` approach for quickly wrapping D3 code in React components.`}</p>
    <p>{`In `}<inlineCode parentName="p">{`renderPreact`}</inlineCode>{`, we then use Preact's `}<inlineCode parentName="p">{`render()`}</inlineCode>{` function to render the Preact component into our anchor div. This works the same way as Preact's normal DOM rendering where you call `}<inlineCode parentName="p">{`render(<App />, document.getElementById('root'))`}</inlineCode>{` to put your app into a root div.`}</p>
    <p>{`The JSX compiles into Preact's `}<inlineCode parentName="p">{`h()`}</inlineCode>{` calls because we didn't mess with the setting, and React refs give us a direct reference to the anchor DOM node.`}</p>
    <p>{`👌`}</p>
    <p><img parentName="p" {...{
        "src": "/1a6e3607d5c335e1ee2015e2cb7b60b2/media-r0tGmQTClFyms-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "the-benchmark"
    }}>{`The benchmark`}</h2>
    <p>{`You can now compare React and Preact side-by-side in my `}<a parentName="p" {...{
        "href": "https://dom-benchmark.now.sh/"
      }}>{`interactive DOM benchmark`}</a>{`. I think it's a fair comparison because Preact handles its own internals.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/preact-dom.gif",
        "alt": null
      }}></img></p>
    <p>{`Numbers are kinda high though. Preact is supposed to be faster than React because it's closer to the metal. I wonder if it's running in dev mode 🤔`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      